import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { btnPrimary, inputDark } from "@/components/shared/AdminKit";
import { useTr } from "@/lib/tr";
import { ProfileFields, Field } from "./AgentFields";
import { PermissionMatrix } from "./PermissionMatrix";
import { LanguagePicker } from "./LanguagePicker";
import { TempPasswordBox } from "./TempPasswordBox";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", job_title: "", department: "", role_description: "", start_date: "", manager_id: "", internal_notes: "",
  password: "", password_confirm: "", generate_password: false, must_change_password: true, status: "active", permissions: [], languages_mode: "selected", allowed_languages: [] };

function Section({ title, children }) {
  return <section className="space-y-3"><h3 className="font-display text-sm font-semibold uppercase tracking-wide text-accent">{title}</h3>{children}</section>;
}

function LoginFields({ tr, form, set }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
        <Checkbox checked={form.generate_password} onCheckedChange={(v) => set("generate_password", !!v)} data-testid="agent-generate-password" />
        {tr("Générer un mot de passe temporaire", "Generate a temporary password")}
      </label>
      {!form.generate_password && <>
        <Field label={tr("Mot de passe initial", "Initial password")} required hint={tr("10 caractères min., lettres et chiffres", "10+ characters, letters and digits")}>
          <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="agent-password" className={inputDark} autoComplete="new-password" />
        </Field>
        <Field label={tr("Confirmation", "Confirm password")} required>
          <input type="password" value={form.password_confirm} onChange={(e) => set("password_confirm", e.target.value)} data-testid="agent-password-confirm" className={inputDark} autoComplete="new-password" />
        </Field>
      </>}
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <Checkbox checked={form.must_change_password || form.generate_password} disabled={form.generate_password} onCheckedChange={(v) => set("must_change_password", !!v)} data-testid="agent-must-change" />
        {tr("Changement obligatoire à la première connexion", "Must change at first login")}
      </label>
      <Field label={tr("Statut initial", "Initial status")}>
        <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="agent-initial-status" className={inputDark}>
          <option value="active">{tr("Actif", "Active")}</option>
          <option value="suspended">{tr("Suspendu", "Suspended")}</option>
        </select>
      </Field>
      <p className="text-xs text-slate-500 sm:col-span-2">{tr("À sa première connexion, l'agent recevra un code sur son adresse professionnelle pour en confirmer la propriété.", "At first login, the agent receives a code on their work email to confirm ownership.")}</p>
    </div>
  );
}

export function CreateAgentDialog({ open, onOpenChange, catalog, managers, canSensitive, onCreated }) {
  const tr = useTr();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = () => { setForm(EMPTY); setTempPw(null); onOpenChange(false); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.generate_password && form.password !== form.password_confirm) { toast.error(tr("Les mots de passe ne correspondent pas", "Passwords do not match")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/agents", { ...form, manager_id: form.manager_id || null, start_date: form.start_date || null });
      toast.success(tr("Agent créé", "Agent created"));
      onCreated();
      if (data.temporary_password) setTempPw(data.temporary_password); else close();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || tr("Création impossible", "Creation failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-4xl overflow-y-auto border-slate-800 bg-slate-950 text-slate-100" data-testid="create-agent-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{tr("Créer un agent", "Create an agent")}</DialogTitle>
          <DialogDescription className="text-slate-400">{tr("Les champs marqués * sont obligatoires.", "Fields marked * are required.")}</DialogDescription>
        </DialogHeader>
        {tempPw ? <TempPasswordBox password={tempPw} onClose={close} /> : (
          <form onSubmit={submit} className="space-y-7">
            <Section title={tr("Informations personnelles et professionnelles", "Personal & professional details")}><ProfileFields tr={tr} form={form} set={set} managers={managers} /></Section>
            <Section title={tr("Informations de connexion", "Login details")}><LoginFields tr={tr} form={form} set={set} /></Section>
            <Section title={tr("Permissions", "Permissions")}><PermissionMatrix catalog={catalog} value={form.permissions} onChange={(v) => set("permissions", v)} canSensitive={canSensitive} /></Section>
            <Section title={tr("Langues autorisées pour le support client", "Allowed customer-support languages")}>
              <LanguagePicker languages={catalog?.languages} mode={form.languages_mode} value={form.allowed_languages} onChange={(m, l) => setForm((f) => ({ ...f, languages_mode: m, allowed_languages: l }))} />
            </Section>
            <div className="flex justify-end gap-3 border-t border-slate-800 pt-4">
              <button type="button" onClick={close} className="rounded-full border border-slate-700 px-5 py-2.5 text-sm hover:bg-slate-800" data-testid="create-agent-cancel">{tr("Annuler", "Cancel")}</button>
              <button disabled={busy} className={btnPrimary} data-testid="create-agent-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{tr("Créer le compte", "Create account")}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
